import { useState, useCallback } from "react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

/**
 * useAutoTranslate — admin helper hook that calls /api/admin/translate
 * to produce Arabic + French versions of an English source string.
 *
 * Usage:
 *   const { translate, busy } = useAutoTranslate();
 *   const { ar, fr } = await translate("Soft cotton t-shirt", "product_name");
 *
 * Backend already ensures fallback to the source string when the LLM
 * misbehaves, so the caller never has to handle "blank translation".
 */
export function useAutoTranslate() {
  const { apiFetch } = useAdminAuth();
  const [busy, setBusy] = useState(false);

  const translate = useCallback(async (text, context = "generic", targets = ["ar", "fr"], opts = {}) => {
    const { silent = false } = opts;
    const src = (text || "").trim();
    if (!src) {
      if (!silent) toast.error("Enter the English text first.");
      return null;
    }
    setBusy(true);
    try {
      const res = await apiFetch("/admin/translate", {
        method: "POST",
        body: JSON.stringify({ text: src, target_langs: targets, context }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Translation failed");
      }
      return await res.json();
    } catch (err) {
      if (!silent) toast.error(err?.message || "Translation failed");
      return null;
    } finally {
      setBusy(false);
    }
  }, [apiFetch]);

  return { translate, busy };
}
